import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as AddIcon } from '../../../assets/icons/add-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/dynamic-pricing/deleted.svg';
import { ReactComponent as Inactive } from '../../../assets/icons/dynamic-pricing/inactive.svg';
import { ReactComponent as AddIconDark } from '../../../assets/icons/iconsDark/add-icon-dark.svg';
import ThemeContext from '../../../contexts/theme-context/ThemeContext';
import {
  UpdateDynamicTimingProperties,
  UpdateTime,
} from '../../../interface/dynamic-pricing.interface';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import CustomTimeInput from '../../../shared-components/custom-time-input/CustomTimePicker';
import DefaultSelectField from '../../../shared-components/default-select-field/DefaultSelectField';
import ConvertToUTC from '../../../shared-functions/ConvertToUTC';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';

interface TimeUpdaterProperties {
  handleCancel: () => void;
  submitFunction: (
    value: UpdateDynamicTimingProperties,
    setLoader: (value: boolean) => void,
  ) => void;
  oldData: { startDateUtc: string; endDateUtc: string; pricingType: string }[];
}

interface UpdateTimeProperties {
  id: string;
  startTime: string;
  endTime: string;
  rate: string;
  action: string;
}

/**
 * Component to update time for the prices
 * @returns
 */
function TimeUpdate(properties: TimeUpdaterProperties) {
  const { t } = useTranslation('dynamicPricing');
  const { theme } = useContext(ThemeContext);
  const { handleCancel, submitFunction, oldData } = properties;
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [listData, setListData] = useState<UpdateTimeProperties[]>(
    oldData?.length
      ? oldData?.map((value, index) => ({
          id: (index + 1).toString(),
          startTime: ConvertUTCtoHHMM(value.startDateUtc),
          endTime: ConvertUTCtoHHMM(value.endDateUtc),
          rate: value.pricingType,
          action: 'action',
        }))
      : [
          {
            id: '1',
            startTime: '00:00',
            endTime: '00:00',
            rate: 'base',
            action: 'action',
          },
        ],
  );
  const rateList = [
    {
      label: t('baseRate'),
      value: 'base',
    },
    {
      label: t('peakRate'),
      value: 'peak',
    },
    {
      label: t('offPeakRate'),
      value: 'offpeak',
    },
  ];
  /**
   * value
   * @param id
   * @param name
   * @param value
   */
  const handleSelect = (id: string, name: string, value: string) => {
    setListData((previousListData) =>
      previousListData.map((item) => (item.id === id ? { ...item, [name]: value } : item)),
    );
  };
  /**
   * add
   * @param value
   * @param index
   */
  const handleAdd = (id: string) => {
    const idList = (parseInt(id, 10) + 1).toString();
    const newListItem = {
      id: idList,
      startTime: '00:00',
      endTime: '10:00',
      rate: 'peak',
      action: 'action',
    };

    setListData([...listData, newListItem]); // Add new object directly
  };
  /**
   * move
   */
  const handleRemove = (value: any) => {
    setListData(listData.filter((item) => item.id !== value.id));
  };
  /**
   * move
   */
  const errorHandling = (output: boolean) => {
    setErrorMessage(output ? '' : t('timeFormatError'));
  };

  /**
   * Handle the submit function
   */
  const submit = () => {
    setLoader(true);
    setErrorMessage('');
    const today = new Date();
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + 1);
    submitFunction(
      {
        timings: listData.map((value) => ({
          startDateUtc: ConvertToUTC(value.startTime),
          endDateUtc:
            ConvertToUTC(value.startTime) > ConvertToUTC(value.endTime)
              ? ConvertToUTC(value.endTime, nextDate.toDateString())
              : ConvertToUTC(value.endTime),
          pricingType: value.rate,
        })),
      },
      setLoader,
    );
  };

  const column = [
    {
      label: t('startTime'),
      accessorKey: 'startTime' as keyof UpdateTime,
      columnWidth: 170,

      /**
       * startTime
       * @param row
       * @param index
       * @returns
       */
      customData: (row: UpdateTime) => (
        <div className="table-row-each-data">
          <div className="start-timer">
            <CustomTimeInput
              name="startTime"
              value={row.startTime}
              onChangeValue={(name, value) => handleSelect(row.id.toString(), name, value)}
              errorHandlingFunction={errorHandling}
              errorHandle={false}
            />
          </div>
        </div>
      ),
    },
    {
      label: t('endTime'),
      accessorKey: 'endTime' as keyof UpdateTime,
      columnWidth: 170,

      /**
       * endTime
       * @param row
       * @returns
       */
      customData: (row: UpdateTime) => (
        <div className="table-row-each-data">
          <div className="end-timer">
            <CustomTimeInput
              name="endTime"
              value={row.endTime}
              onChangeValue={(name, value) => handleSelect(row.id.toString(), name, value)}
              errorHandlingFunction={errorHandling}
              errorHandle={false}
            />
          </div>
        </div>
      ),
    },
    {
      label: t('rate'),
      accessorKey: 'rate' as keyof UpdateTime,
      columnWidth: 170,
      /**
       * row
       * @param row
       * @returns
       */
      customData: (row: UpdateTime) => (
        <div className="table-row-each-data">
          <div className="select-rate">
            <DefaultSelectField
              name="rate"
              selectedValue={row.rate}
              onChangeValue={(name, value) => handleSelect(row.id.toString(), name, value)}
              options={rateList}
            />
          </div>
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof UpdateTime,
      columnWidth: 90,
      /**
       * image
       * @param row
       * @returns
       */
      customData: (row: UpdateTime, index: number) => (
        <div className="table-row-each-data">
          <div className="action-icons">
            <div
              className="create-icon"
              role="presentation"
              onClick={() => handleAdd(row.id.toString())}
              style={{ display: listData.length - 1 === index ? 'flex' : 'none' }}>
              {theme === 'light' ? <AddIcon /> : <AddIconDark />}
            </div>
            <div
              className="delete-icon"
              role="presentation"
              onClick={() => handleRemove(row)}
              style={{ pointerEvents: listData.length - 1 > 0 ? 'auto' : 'none' }}>
              {listData.length - 1 > 0 ? <DeleteIcon /> : <Inactive />}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="pricing-time-update-container">
      <div className="time-title">{t('updateTime')}</div>
      <div className="time-update-main-content">
        <CustomTable
          header={column}
          data={listData}
          loading={false}
          filterAllow={false}
          sizeChangeButtonRequired={false}
          limit={0}
          currentPage={0}
          totalPages={0}
          totalResults={0}
          topHeader={false}
          handlePageChange={() => {}}
          handleLimitChange={() => {}}
        />
        <p className="text-danger">{errorMessage}</p>
      </div>

      <div className="time-buttons">
        <div className="time-back-button">
          <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleCancel} />
        </div>
        <div className="time-set-button">
          <MyButton
            label={t('apply')}
            buttonType="submit"
            onClickFunc={submit}
            disableButton={loader}
            loading={loader}
          />
        </div>
      </div>
    </div>
  );
}

export default TimeUpdate;
